import { EditorState, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import React, { useEffect, useState, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { EditorToolbarOptions } from '@debbie/common/dist/constants.js';
import { UnknownIntent, } from '@debbie/common/dist/intents/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions, } from '@debbie/common/dist/templates/suggestions.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import * as templatesAPI from '@debbie/cortex/dist/api/templates.js';
import AutoCallActions from '../../AutoCallActions.js';
import { Panel, Intent, Button } from '../../index.js';
import THEME from '../../../theme.js';
import { encodeText } from '../../../utils/templates.js';
import { Header, PreviewModal, SettingsModal, VersionsModal, CopyTemplateModal, AddTagModal, } from './index.js';
import { selectUpdateTypes } from '@debbie/cortex/dist/update-types/selectors.js';
import { Section } from '../../Panel.js';
import { ExternalLink } from '../../Icons/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
const TemplateEditor = (props) => {
    const lang = useSelector((s) => s.lang);
    const editorRef = useRef(null);
    const updateTypes = useSelector(selectUpdateTypes);
    const tenant = useSelector(selectTenant);
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const [state, setState] = useState({
        defaultTemplate: null,
        defaultEditorState: EditorState.createEmpty(),
        defaultSubjectState: EditorState.createEmpty(),
        defaultIntent: UnknownIntent(),
        editorState: EditorState.createEmpty(),
        subjectState: EditorState.createEmpty(),
        intent: UnknownIntent(),
        template: null,
        loading: false,
        settingsModalOpen: false,
        previewModalOpen: false,
        versionsModalOpen: false,
        copyTemplateModalOpen: false,
        addTagModalOpen: false,
        changesNotSaved: false,
        error: null,
        mentionSuggestions: [],
    });
    useEffect(() => {
        // @ts-ignore
        editorRef.current?.focusEditor();
        loadTemplate(props);
    }, []);
    useEffect(() => {
        loadTemplate(props);
    }, [props.selectedLanguage]);
    useEffect(() => {
        c.updates.types();
    }, []);
    useEffect(() => {
        checkChangesState();
    }, [state]);
    const loadTemplate = async (props) => {
        if (!state.loading) {
            setState({ ...state, loading: true });
            try {
                const template = await templatesAPI.getTemplate(props.templateId, props.ac, props.selectedLanguage, true);
                const mentionSuggestions = getMentionSuggestions(lang, template.type, propertyTypes || undefined, tenant);
                const latest = template;
                const subjectBlock = htmlToDraft.default(encodeText(latest.subject || '', mentionSuggestions));
                const subjectState = ContentState.createFromBlockArray(subjectBlock.contentBlocks);
                const contentBlock = htmlToDraft.default(encodeText(latest.html || '', mentionSuggestions));
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                const intent = latest.intent;
                const editorState = EditorState.createWithContent(contentState);
                const subject = EditorState.createWithContent(subjectState);
                // const subjectValidationErrors = templateValidator(subject, lang)
                // const editorValidationErrors = templateValidator(editorState, lang)
                setState({
                    ...state,
                    intent,
                    editorState,
                    subjectState: subject,
                    loading: false,
                    template: latest,
                    defaultTemplate: latest,
                    defaultEditorState: editorState,
                    defaultSubjectState: subject,
                    defaultIntent: intent,
                    mentionSuggestions,
                    // subjectValidationErrors,
                    // editorValidationErrors,
                });
            }
            catch (e) {
                setState({ ...state, loading: false, error: e?.message });
            }
        }
    };
    const checkChangesState = () => {
        const settingsAreChanged = JSON.stringify(state.template) !== JSON.stringify(state.defaultTemplate);
        if (!state.defaultEditorState || !state.defaultSubjectState) {
            return;
        }
        const editorIsChanged = state.editorState.getCurrentContent().getPlainText() !==
            state.defaultEditorState.getCurrentContent().getPlainText();
        const subjectIsChanged = state.subjectState.getCurrentContent().getPlainText() !==
            state.defaultSubjectState.getCurrentContent().getPlainText();
        const intentChanged = JSON.stringify(state.intent) !== JSON.stringify(state.defaultIntent);
        const changesNotSaved = settingsAreChanged || editorIsChanged || subjectIsChanged || intentChanged;
        if (changesNotSaved !== state.changesNotSaved) {
            // const subjectValidationErrors = templateValidator(
            //   state.subjectState,
            //   lang,
            // )
            // const editorValidationErrors = templateValidator(state.editorState, lang)
            setState({
                ...state,
                changesNotSaved: changesNotSaved,
                // subjectValidationErrors,
                // editorValidationErrors,
            });
        }
    };
    const loadInEditor = (template) => {
        const contentBlock = htmlToDraft.default(encodeText(template.html || '', state.mentionSuggestions));
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const editorState = EditorState.createWithContent(contentState);
        // const editorValidationErrors = templateValidator(editorState, lang)
        setState({
            ...state,
            template: template,
            editorState,
            // editorValidationErrors,
        });
    };
    const onEditorStateChange = (editorState) => {
        // const editorValidationErrors = templateValidator(editorState, lang)
        // setState({ ...state, editorState: editorState, editorValidationErrors })
        setState({ ...state, editorState: editorState });
    };
    const onSubjectStateChange = (editorState) => {
        // const subjectValidationErrors = templateValidator(editorState, lang)
        // setState({ ...state, subjectState: editorState, subjectValidationErrors })
        setState({ ...state, subjectState: editorState });
    };
    const onIntentChange = (intent) => {
        setState({ ...state, intent });
    };
    const mentions = state.template
        ? state.mentionSuggestions.map((suggestion) => ({
            value: suggestion.label,
            text: suggestion.label,
            url: suggestion.label,
        }))
        : [];
    const inUpdateTypes = updateTypes &&
        updateTypes.filter((updateType) => updateType.procedure?.find(
        // @ts-ignore
        (todo) => JSON.stringify(todo.details).indexOf(props.templateId) !== -1));
    return (React.createElement("div", null, state.error ? (React.createElement(Panel, { header: "Error getting template" }, state.error)) : (React.createElement("div", null,
        state.template && (React.createElement(Header, { mentionSuggestions: state.mentionSuggestions, templateId: props.templateId, selectedLanguage: props.selectedLanguage, ac: props.ac, template: state.template, setTemplate: (template) => {
                setState({ ...state, template });
                loadTemplate(props);
            }, subjectState: state.subjectState, editorState: state.editorState, intent: state.intent, changesNotSaved: state.changesNotSaved, toggleModal: (name) => {
                setState({
                    ...state,
                    // Find a better solution for better types
                    //@ts-ignore
                    [`${name}ModalOpen`]: !state[`${name}ModalOpen`],
                });
            }, loading: state.loading, defaultTemplate: state.defaultTemplate })),
        state.template &&
            [TemplateType.EMAIL, TemplateType.EBOKS].includes(state.template.type) && (React.createElement(EmailSubjectEditor, null,
            React.createElement(Editor, { toolbar: { options: EditorToolbarOptions }, editorState: state.subjectState, onEditorStateChange: onSubjectStateChange, stripPastedStyles: true, mention: {
                    separator: ' ',
                    trigger: '@',
                    suggestions: mentions,
                } }))),
        React.createElement(Editor, { toolbarClassName: state.template && state.template.type === TemplateType.SMS
                ? 'hide-toolbar'
                : '', stripPastedStyles: true, toolbar: { options: EditorToolbarOptions }, editorState: state.editorState, onEditorStateChange: onEditorStateChange, wrapperStyle: { marginBottom: '20px' }, ref: editorRef, mention: {
                separator: ' ',
                trigger: '@',
                suggestions: mentions,
            } }),
        state.template && state.template.type === TemplateType.AUTOCALL && (React.createElement("div", null,
            React.createElement(AutoCallActions, { actions: state.template.details.actions || [], onChange: (actions) => {
                    if (!state.template)
                        return;
                    setState({
                        ...state,
                        // @ts-ignore
                        template: {
                            ...state.template,
                            details: {
                                ...state.template.details,
                                actions,
                            },
                        },
                    });
                } }),
            React.createElement("p", { onClick: () => {
                    if (state.template &&
                        state.template.type === TemplateType.AUTOCALL) {
                        setState({
                            ...state,
                            template: {
                                ...state.template,
                                details: {
                                    ...state.template.details,
                                    actions: (state.template.details.actions || []).concat({
                                        procedureId: 'f892b601-2d51-46e6-901c-1b90556dcdf3', //Empty procedure
                                        id: Math.random().toLocaleString(),
                                    }),
                                },
                            },
                        });
                    }
                }, style: {
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    fontWeight: 900,
                    color: THEME.COLOR_ACCENT,
                    marginTop: '0px',
                } }, lang.autoCall.addAction))),
        state.intent && (React.createElement(Panel, null,
            React.createElement(Intent, { intent: state.intent, editable: !state.defaultTemplate?.public, onEdit: (intent) => {
                    onIntentChange(intent);
                } }))),
        inUpdateTypes && !!inUpdateTypes.length && (React.createElement(Panel, null,
            React.createElement(Section, { header: lang.templates.updateTypesUsingThisTemplate, description: lang.templates.updateTypesUsingThisTemplate },
                React.createElement("ul", null, inUpdateTypes.map((updateType) => (React.createElement("li", { key: updateType.updateTypeId, style: {
                        cursor: 'pointer',
                    }, onClick: () => c.redirect.redirect(`/settings/workflows/tasks/${updateType.updateTypeId}`) },
                    updateType.label,
                    ' ',
                    React.createElement(ExternalLink, { style: {
                            margin: '0.1em 0 -0.1em',
                        } })))))))),
        React.createElement(Button, { size: "small", theme: "dangerous", disabled: (inUpdateTypes?.length || 0) > 0 || props.preventDelete, onClick: async () => {
                if (window.confirm(lang.templates.confirmDelete)) {
                    try {
                        await templatesAPI.del(props.templateId);
                        await c.templates.getAll();
                        c.redirect.redirect('/settings/template-editor');
                    }
                    catch (error) {
                        alert('Error');
                    }
                }
            } }, lang.templates.delete),
        state.previewModalOpen && (React.createElement(PreviewModal, { toggleModal: () => setState({
                ...state,
                previewModalOpen: !state.previewModalOpen,
            }), templateId: props.templateId, template: state.template, editorState: state.editorState, subjectState: state.subjectState, caseId: props.caseId, setCaseId: props.setCaseId })),
        state.settingsModalOpen && state.template && (React.createElement(SettingsModal, { toggleSettingsModal: () => setState({
                ...state,
                settingsModalOpen: !state.settingsModalOpen,
            }), template: state.template, setTemplate: (template) => {
                setState({ ...state, template });
            } })),
        state.versionsModalOpen && state.template && (React.createElement(VersionsModal, { toggleVersionsModal: () => setState({
                ...state,
                versionsModalOpen: !state.versionsModalOpen,
            }), ac: props.ac, loadInEditor: loadInEditor, selectedLanguage: props.selectedLanguage, template: state.template, templateId: props.templateId, defaultTemplate: state.defaultTemplate })),
        state.addTagModalOpen && (React.createElement(AddTagModal, { templateId: props.templateId, toggleTagModal: async () => setState({
                ...state,
                addTagModalOpen: !state.addTagModalOpen,
            }), loadTemplate: async () => {
                await loadTemplate(props);
            } })),
        state.copyTemplateModalOpen && state.template && (React.createElement(CopyTemplateModal, { toggleModal: () => setState({
                ...state,
                copyTemplateModalOpen: !state.copyTemplateModalOpen,
            }), loadTemplate: () => loadTemplate(props), template: state.template, templateId: props.templateId }))))));
};
export default TemplateEditor;
const EmailSubjectEditor = styled.div `
  .rdw-editor-toolbar {
    display: none;
    position: sticky;
    top: 0px;
    z-index: 999;
  }
  .rdw-editor-wrapper {
    .rdw-editor-main {
      min-height: 45px;
      border-top: 1px solid #ccc;
      border-top-left-radius: 3px;
      border-top-right-radius: 3px;

      .public-DraftStyleDefault-block {
        margin: 0;
      }
    }
  }
`;
//# sourceMappingURL=TemplateEditor.js.map